import type { ReportDocumentBlockV1, ReportDocumentModelV1 } from '../../types/reportContractsV2';
import type { SiteDiligenceReportEvidence } from '../../types/siteRag';
import type { SiteResourceView } from '../../types/siteResourceEvidence';
import { canonicalJson } from '../reportGeneration/reportContractHash';
import { readSiteResourceView, siteResourceDetails } from '../sed/siteResourceDocumentProjection';

export const SITE_RESOURCE_STATEMENTS_BLOCK_ID = 'site-resource-statements-v1';
export const SITE_RESOURCE_STATEMENTS_DISPLAY_BLOCK_ID = `${SITE_RESOURCE_STATEMENTS_BLOCK_ID}:display`;
const RESOURCE_LABEL = 'Source-stated usable land and site water';

/** Two hash-bound appendix atoms: the strict scalar carrier and its complete
 * display text. No private source witness or other diligence block is copied. */
export function buildSiteResourceDocumentBlocks(value: unknown): ReportDocumentBlockV1[] {
  const view = readSiteResourceView(value);
  if (!view) return [];
  return [
    {
      id: SITE_RESOURCE_STATEMENTS_BLOCK_ID,
      kind: 'structured-data',
      label: RESOURCE_LABEL,
      value: view,
      sourceIds: [],
      citationIds: [],
    },
    {
      id: SITE_RESOURCE_STATEMENTS_DISPLAY_BLOCK_ID,
      kind: 'list',
      ordered: false,
      items: siteResourceDetails(view).map((row, index) => ({
        id: `${SITE_RESOURCE_STATEMENTS_DISPLAY_BLOCK_ID}:${index + 1}`,
        text: `${row.label}: ${row.value}`,
      })),
      sourceIds: [],
      citationIds: [],
    },
  ];
}

/** Read only the exact immutable owned pair, before public redaction. A
 * malformed pair must not become valid through removal of private keys. */
export function readSiteResourceStatementsFromDocument(
  document: ReportDocumentModelV1,
): SiteResourceView | undefined {
  const owned = document.sections.flatMap(section => section.blocks
    .filter(block => block.id === SITE_RESOURCE_STATEMENTS_BLOCK_ID
      || block.id === SITE_RESOURCE_STATEMENTS_DISPLAY_BLOCK_ID)
    .map(block => ({ sectionId: section.sectionId, block })));
  if (owned.length === 0) return undefined;
  const carrier = owned.find(entry => entry.block.id === SITE_RESOURCE_STATEMENTS_BLOCK_ID);
  if (owned.length !== 2 || owned.some(entry => entry.sectionId !== 'appendix')
    || !carrier || carrier.block.kind !== 'structured-data') {
    throw new Error('Frozen site-resource statements have an invalid appendix contract.');
  }
  const view = readSiteResourceView(carrier.block.value);
  const expected = buildSiteResourceDocumentBlocks(view);
  if (expected.length !== 2 || expected.some(block => {
    const actual = owned.find(entry => entry.block.id === block.id)?.block;
    return !actual || canonicalJson(actual) !== canonicalJson(block);
  })) {
    throw new Error('Frozen site-resource display text disagrees with its scalar carrier.');
  }
  return view;
}

/** A render-only resource subsection, derived only from the strict frozen
 * view. This deliberately does not restore generic siteDiligenceEvidence or
 * its original private sources, quotes, identities, hashes or paths. */
export function siteResourceRenderEvidence(value: SiteResourceView): SiteDiligenceReportEvidence {
  const view = readSiteResourceView(value);
  if (!view) throw new Error('Frozen site-resource statements are required.');
  const rows = siteResourceDetails(view);
  return {
    resourceStatements: view,
    blocks: [{
      id: 'siteResources',
      label: RESOURCE_LABEL,
      qualification: view.facts.length ? 'estimated' : 'unknown',
      facts: rows.filter(row => row.label !== 'Resource scope').map(row => `${row.label}: ${row.value}`),
      caveats: rows.filter(row => row.label === 'Resource scope').map(row => row.value),
      sources: [],
    }],
  };
}
